/**
 * riskConverters.js
 *
 * DashboardRiskManagementPageから抽出された純粋関数群
 * リスク管理固有のデータ変換・ID解決ロジックを提供
 */

import { resolveEmployeeIds } from './converters.js';

/**
 * 複数キーに対して、行データから一意な値を抽出してセレクトボックス用のオプションを生成（リスク管理用）
 * @param {Array} rows - データ行の配列
 * @param {Array} keys - 抽出対象のキー名配列
 * @returns {Object} キーごとのオプション配列 { key: ['', value1, value2, ...] }
 * @note owner フィールドはスキップする（tags型のため）
 */
export function buildSelectOptions(rows, keys) {
  const optionsByKey = Object.fromEntries(keys.map((key) => [key, new Set()]));

  rows.forEach((row) => {
    keys.forEach((key) => {
      const value = row[key];
      if (value === undefined || value === null || value === '') return;

      if (key === 'owner') {
        return;
      }

      optionsByKey[key].add(value);
    });
  });

  return Object.fromEntries(
    Object.entries(optionsByKey).map(([key, values]) => [key, ['', ...Array.from(values)]]),
  );
}

/**
 * 小分類の名前からIDを引くMapを生成（リスク管理用）
 * @param {Array} smallCategories - 小分類データの配列
 * @returns {Object} { pairMap: Map<'middleId::small', id>, byNameMap: Map<'small', id> }
 */
export function buildSmallCategoryLookup(smallCategories) {
  const pairMap = new Map();
  const byNameMap = new Map();
  smallCategories.forEach((sc) => {
    const name = sc.small_category;
    const midId = sc.middle_category?.id || sc.middle_category_id;
    if (name && midId) {
      pairMap.set(`${midId}::${name}`, sc.id);
    }
    if (name && !byNameMap.has(name)) {
      byNameMap.set(name, sc.id);
    }
  });
  return { pairMap, byNameMap };
}

/**
 * 大分類名ごとに中分類の選択肢を生成（リスク管理・課題管理用）
 * @param {Array} items - 中分類マスタデータの配列（{ middle_category, large_category: { name } } 形状）
 * @returns {Object} { largeName: ['', middle1, middle2, ...] }
 * @note converters.js の同名関数は進捗行形状（large_category_name/middle_category_name）用。
 *       こちらは getMiddleCategories() が返すマスタのネスト形状を読む
 */
export function buildMiddleCategoryOptionsByLarge(items) {
  const map = new Map();

  items.forEach((item) => {
    const largeName = item.large_category?.name;
    const middleName = item.middle_category;

    if (!largeName || !middleName) return;

    if (!map.has(largeName)) {
      map.set(largeName, new Set());
    }
    map.get(largeName).add(middleName);
  });

  return Object.fromEntries(
    Array.from(map.entries()).map(([large, middleSet]) => [large, ['', ...Array.from(middleSet)]]),
  );
}

/**
 * 中分類の名前からIDを引くMapを生成（リスク管理用）
 * @param {Array} items - 中分類データの配列
 * @returns {Object} { pairMap: Map<'large::middle', id>, byNameMap: Map<'middle', id> }
 */
export function buildMiddleCategoryIdLookup(items) {
  const pairMap = new Map();
  const byNameMap = new Map();
  items.forEach((item) => {
    const middleName = item.middle_category;
    const largeName = item.large_category?.name;
    if (middleName) {
      if (!byNameMap.has(middleName)) {
        byNameMap.set(middleName, item.id);
      }
      if (largeName) {
        pairMap.set(`${largeName}::${middleName}`, item.id);
      }
    }
  });
  return { pairMap, byNameMap };
}

/**
 * リスクレコードデータをAPIペイロード形式に変換
 * @param {Object} row - 画面表示用レコード
 * @param {Object} refs - マスタデータのルックアップMap
 * @param {Object} defaults - デフォルト値（oemId, regionId）
 * @returns {Object|null} APIペイロード（必須項目不足時はnull）
 */
export function toPayload(row, refs, defaults) {
  const title = row.title || '';
  const statusId = refs.statusByName.get(row.status);
  const actorId = refs.actorByName.get(row.actor);
  let oemId = defaults.oemId;
  let regionId = defaults.regionId;
  if (row.oemRegion && refs.oemRegionLookup.has(row.oemRegion)) {
    const resolved = refs.oemRegionLookup.get(row.oemRegion);
    oemId = resolved.oemId;
    regionId = resolved.regionId;
  }

  const causeId = row.riskOrigin ? (refs.causeByName.get(row.riskOrigin) || null) : null;
  const impactId = row.impact ? (refs.impactByName.get(row.impact) || null) : null;
  const probabilityId = row.probability ? (refs.probabilityByName.get(row.probability) || null) : null;
  const employeeIds = resolveEmployeeIds(row.owner, refs.employeeByName);

  if (!title || !statusId || !actorId || !oemId || !regionId || !causeId || !impactId || !probabilityId || !row.dueDate || employeeIds.length === 0) {
    return null;
  }

  // Large category ID 解決
  const largeCategoryId = row.largeCategory
    ? (refs.largeCategoryByName.get(row.largeCategory) || null)
    : null;

  // Middle category ID 解決
  let middleCategoryId = null;
  if (row.middleCategory) {
    const largeKey = row.largeCategory || '';
    const middleKey = `${largeKey}::${row.middleCategory}`;
    middleCategoryId = refs.middleCategoryIdLookup.pairMap.get(middleKey)
      || refs.middleCategoryIdLookup.byNameMap.get(row.middleCategory)
      || null;
  }

  // Small category ID 解決
  let smallCategoryId = null;
  if (row.smallItem && middleCategoryId) {
    smallCategoryId = refs.smallCategoryLookup.pairMap.get(`${middleCategoryId}::${row.smallItem}`) || null;
  }
  if (!smallCategoryId && row.smallItem) {
    smallCategoryId = refs.smallCategoryLookup.byNameMap.get(row.smallItem) || null;
  }

  return {
    title,
    deadline: row.dueDate,
    oem_id: oemId,
    region_id: regionId,
    actor_id: actorId,
    status_id: statusId,
    cause_id: causeId,
    impact_id: impactId,
    probability_id: probabilityId,
    policy_id: row.responsePolicy ? (refs.policyByName.get(row.responsePolicy) || null) : null,
    large_category_id: largeCategoryId,
    middle_category_id: middleCategoryId,
    small_category_id: smallCategoryId,
    is_all_problem: Boolean(row.projectRisk),
    employee_ids: employeeIds,
    pmo_confirm_completed_at: row.pmoConfirmCompletedAt || null,
    policy_decision_deadline: row.policyDecisionDeadline || null,
    policy_decision_date: row.policyDecisionDate || null,
    policy_detail: row.policyDetail || '',
    response_start_date: row.responseStartDate || null,
    actor_completed_at: row.actorCompletedAt || null,
    emergence_issue_date: row.emergenceIssueDate || null,
    updated_at_iso: row.updated_at_iso ?? null,
  };
}
